import {
  CurecallSearchableSelect,
  CurecallButton,
  ECurecallButtonAppearance,
  CurecallSearchableSelectItem,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { EVariableType, TVariable } from 'src/types/variable'
import { useMemo, useState, useRef, useEffect } from 'react'
import { useProjectVariables } from '../use-project-variables.hook'
import { twMerge } from 'src/utils/tw-merge.util'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { useLocale } from 'src/hooks/use-locale'
import { useCurrentProject } from 'src/providers/current-project.provider'

interface ExistingVariableFormProps {
  onCancel: () => void
  onValidate: (variable: TVariable) => void
  onSwitchToNewVariable: () => void
}
export function ExistingVariableForm({
  onCancel,
  onValidate,
  onSwitchToNewVariable,
}: Readonly<ExistingVariableFormProps>) {
  const { t } = useTranslation()
  const { data: variables = [] } = useProjectVariables()
  const { locale } = useLocale()
  const { isFeatureActivated } = useCurrentProject()

  const [selectedVariable, setSelectedVariable] = useState<TVariable | null>(null)
  const [showError, setShowError] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Focus the first input when component mounts
    const firstInput = containerRef.current?.querySelector('input')
    firstInput?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleValidateExisting()
    }
  }

  const getVariableCategory = (type: EVariableType): string | undefined => {
    if (type === EVariableType.BOOLEAN) {
      return t('pages.configuration.followups.chatbot.variable-modal.category-response-yes-no')
    }
    if (type === EVariableType.INTEGER_VALUE_RANGE) {
      return t('pages.configuration.followups.chatbot.variable-modal.category-response-integer-range')
    }
    if (type === EVariableType.MEDIA) {
      return t('pages.configuration.followups.chatbot.variable-modal.category-response-media')
    }
    if (type === EVariableType.STRING) {
      return t('pages.configuration.followups.chatbot.variable-modal.category-response-free-text')
    }
    return undefined
  }

  const VARIABLE_OPTIONS = useMemo(
    () =>
      variables
        .filter(
          (v: TVariable) =>
            v.type === EVariableType.BOOLEAN ||
            v.type === EVariableType.INTEGER_VALUE_RANGE ||
            (v.type === EVariableType.MEDIA && isFeatureActivated('patient-documents')) ||
            v.type === EVariableType.STRING,
        )
        .map((variable: TVariable) => ({
          label: getTranslatedLabel(locale, variable.intlLabel),
          value: variable.id,
          category: getVariableCategory(variable.type),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, variables, isFeatureActivated],
  )

  const handleVariableSelect = (variable: CurecallSearchableSelectItem) => {
    showError && setShowError(false)
    const foundVariable = variables.find((v: TVariable) => v.id === variable.value)
    setSelectedVariable(foundVariable || null)
  }

  const handleValidateExisting = () => {
    if (selectedVariable) {
      onValidate(selectedVariable)
    } else {
      setShowError(true)
    }
  }

  const getVariablePreviewResponse = (variable: TVariable): string => {
    if (variable.type === EVariableType.BOOLEAN) {
      return t('pages.configuration.followups.chatbot.variable-modal.variable-preview-response-yes-no')
    }
    if (variable.type === EVariableType.INTEGER_VALUE_RANGE) {
      return t(
        'pages.configuration.followups.chatbot.variable-modal.variable-preview-response-integer-range',
        {
          min: variable.minValue ?? 0,
          max: variable.maxValue ?? 0,
        },
      )
    }
    if (variable.type === EVariableType.MEDIA) {
      return t('pages.configuration.followups.chatbot.variable-modal.variable-preview-response-media')
    }
    if (variable.type === EVariableType.STRING) {
      return t('pages.configuration.followups.chatbot.variable-modal.variable-preview-response-free-text')
    }
    return ''
  }

  if (VARIABLE_OPTIONS.length === 0) {
    return (
      <div className="c-flex c-flex-col c-items-center c-justify-center c-py-8 c-px-4">
        <div className="c-text-center c-max-w-md">
          <h3 className="c-text-lg c-font-semibold c-text-gray-900 c-mb-2">
            {t('pages.configuration.followups.chatbot.variable-modal.no-variables-available')}
          </h3>
          <p className="c-text-sm c-text-gray-600 c-mb-6">
            {t('pages.configuration.followups.chatbot.variable-modal.no-variables-message')}
          </p>
          <div className="c-flex c-justify-center c-gap-8">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onCancel}>
              {t('common.actions.cancel')}
            </CurecallButton>
            <CurecallButton onClick={onSwitchToNewVariable}>
              {t('pages.configuration.followups.chatbot.variable-modal.create-first-variable')}
            </CurecallButton>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown}>
      <div className="c-flex c-flex-col c-gap-6">
        <CurecallSearchableSelect
          label={t('pages.configuration.followups.chatbot.variable-modal.choose-existing-variable')}
          placeholder={t('pages.configuration.followups.chatbot.variable-modal.variable-placeholder')}
          items={VARIABLE_OPTIONS}
          onSelect={handleVariableSelect}
          defaultItemSelected={
            selectedVariable ? VARIABLE_OPTIONS.find((opt) => opt.value === selectedVariable.id) : undefined
          }
          optionsClassName="!c-max-h-44"
          autoComplete="off"
          errorMessage={showError ? t('common.form.required') : undefined}
        />
      </div>

      {selectedVariable && (
        <div className="c-mt-4 c-border c-border-gray-200 c-rounded-lg c-p-4 c-bg-gray-50">
          <p className="c-text-sm c-mb-1 c-font-semibold c-text-gray-900">
            {getTranslatedLabel(locale, selectedVariable.intlLabel)}
          </p>
          <p className="c-text-sm c-mb-0 c-text-gray-700">{getVariablePreviewResponse(selectedVariable)}</p>
        </div>
      )}

      <div className={twMerge('c-flex c-justify-center c-gap-8 c-pt-32', selectedVariable && 'c-pt-10')}>
        <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onCancel}>
          {t('common.actions.cancel')}
        </CurecallButton>
        <CurecallButton onClick={handleValidateExisting}>
          {t('pages.configuration.followups.chatbot.variable-modal.associate-variable')}
        </CurecallButton>
      </div>
    </div>
  )
}
